import { Trans } from '@lingui/react/macro';
import type { PremiumPricing } from '@/utils/premiumPricing';

interface PremiumSubscribeButtonsProps {
  pricing: PremiumPricing;
  onSubscribe: (plan: 'monthly' | 'yearly') => void;
}

/**
 * Monthly/yearly Premium checkout buttons with the discount-aware "Save $X/year"
 * note. Shared by the Settings > Premium page and the premium upsell modal so
 * both offer identical pricing and layout.
 */
function PremiumSubscribeButtons({ pricing, onSubscribe }: PremiumSubscribeButtonsProps) {
  const { monthly, yearly, yearlySavings } = pricing;

  return (
    <div>
      <div className="flex flex-col gap-4 sm:flex-row">
        <button
          type="button"
          onClick={() => onSubscribe('monthly')}
          className="group bg-surface-primary flex flex-1 cursor-pointer items-center justify-center gap-1 rounded-lg border-2 border-[var(--color-link)] px-6 py-2 text-center transition-all hover:bg-[var(--color-link)] hover:shadow-lg focus-visible:ring-2 focus-visible:ring-[var(--color-link)] focus-visible:ring-offset-2 focus-visible:outline-none"
        >
          <div className="text-link text-xl font-bold transition-colors group-hover:text-white">
            {monthly}
          </div>
          <div className="text-link text-sm transition-colors group-hover:text-white">
            <Trans>per month</Trans>
          </div>
        </button>

        <button
          type="button"
          onClick={() => onSubscribe('yearly')}
          className="flex-1 cursor-pointer rounded-lg border-2 border-[var(--color-link)] bg-[var(--color-link)] px-6 py-2 text-center text-white transition-all hover:bg-[var(--color-link-hover)] hover:shadow-lg focus-visible:ring-2 focus-visible:ring-[var(--color-link)] focus-visible:ring-offset-2 focus-visible:outline-none"
        >
          <div className="flex items-center justify-center gap-1">
            <div className="text-xl font-bold">{yearly}</div>
            <div className="text-sm">
              <Trans>per year</Trans>
            </div>
          </div>
          <div className="text-xs opacity-90">
            <Trans>Save {yearlySavings}/year</Trans>
          </div>
        </button>
      </div>
      <p className="text-secondary mt-4 text-center text-sm">
        <Trans>Cancel anytime. No commitments.</Trans>
      </p>
    </div>
  );
}

export default PremiumSubscribeButtons;
